import React from 'react';
import { Button, Menu, List, ListItemButton, ListItemText, Collapse, ListItemIcon } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import unitsData from '../data/units_merged.json';
import navMenuData from '../data/navigation_menu_authoritative.json';
import { Link as RouterLink } from 'react-router-dom';
import { slugToNavId } from '../utils/navIds';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS, buildApiActionUrl } from '../utils/apiConfig';

const API_BASE_URL = API_ENDPOINTS.api;

const unitContext = (require as any).context('../pages/units', false, /\.tsx$/);
const AVAILABLE_UNIT_SLUGS: string[] = unitContext
  .keys()
  .map((key: string) => key.replace('./', '').replace(/\.tsx$/, ''))
  .sort((a: string, b: string) => a.length - b.length);

const slugOverrides: Record<string, string> = {
  PROGRAMMIERUNG_DATENTYPEN: 'm10_u1_x1_',
  PROGRAMMIERUNG_FUNKTIONEN: 'm10_u1_x2_funktionen',
  PROGRAMMIERUNG_AUFGABEN_FUP: 'm10_u2_x1_fup_uebung',
  PROGRAMMIERUNG_AUFGABEN_CAD: 'm10_u2_x2_CAD',
  // M50 Step-by-Step Seiten
  M50_U1_X1: 'm50_u1_x1_stepbystep',
  M50_U1_X2: 'm50_u1_x2_stepbystep',
  M50_U1_X3: 'm50_u1_x3_stepbystep',
  M50_U1_X4: 'm50_u1_x4_stepbystep',
  M50_U1_X5: 'm50_u1_x5_stepbystep',
  M50_U1_X6: 'm50_u1_x6_stepbystep',
  M50_U1_X7: 'm50_u1_x7_stepbystep',
  M50_U1_X8: 'm50_u1_x8_stepbystep',
  M50_U1_X9: 'm50_u1_x9_stepbystep',
  M50_U1_X10: 'm50_u1_x10_stepbystep'
};

const actionSlugOverrides: Record<string, string> = {
  ACTION_CODESYS: slugOverrides.PROGRAMMIERUNG_DATENTYPEN,
  ACTION_CODESYS2: slugOverrides.PROGRAMMIERUNG_FUNKTIONEN,
  ACTION_CODESYS100: slugOverrides.PROGRAMMIERUNG_AUFGABEN_FUP,
  ACTION_CODESYS200: slugOverrides.PROGRAMMIERUNG_AUFGABEN_CAD
};

const SLUG_BY_ID: Record<string, string> = {};
const SLUG_PAIRS: Array<{ id: string; slug: string }> = [];

AVAILABLE_UNIT_SLUGS.forEach(slug => {
  const navId = slugToNavId(slug);
  if (!navId) return;
  SLUG_PAIRS.push({ id: navId, slug });
  const existing = SLUG_BY_ID[navId];
  if (!existing || slug.length < existing.length) {
    SLUG_BY_ID[navId] = slug;
  }
});

const resolveNavIdToSlug = (id: string): string | null => {
  const override = slugOverrides[id];
  if (override) return override;

  const direct = SLUG_BY_ID[id];
  if (direct) return direct;

  const lowerId = id.toLowerCase();
  const similar = SLUG_PAIRS.find(pair => pair.id.toLowerCase() === lowerId);
  if (similar) return similar.slug;

  const prefixed = SLUG_PAIRS.filter(pair => pair.id.startsWith(id));
  if (prefixed.length) {
    let bestSlug: string | null = null;
    prefixed.forEach(current => {
      if (!bestSlug || current.slug.length < bestSlug.length) {
        bestSlug = current.slug;
      }
    });
    if (bestSlug) return bestSlug;
  }

  const loose = SLUG_PAIRS.find(pair => pair.id.toLowerCase().includes(lowerId));
  if (loose) return loose.slug;

  // Dynamischer Quiz-Fallback: IDs enden auf _X99 oder _U99 -> generiere slug + _quiz
  if (/_[XU]99$/i.test(id)) {
    return id.toLowerCase() + '_quiz';
  }
  return null;
};

const ModuleDropdown: React.FC = () => {
  const { totalScore, token, canEditContent } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [expandedModules, setExpandedModules] = React.useState<Record<string, boolean>>({});
  const [unitOverrides, setUnitOverrides] = React.useState<Record<string, any>>({});

  const open = Boolean(anchorEl);
  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // Lade alle M50 Unit Overrides beim Öffnen des Menüs
  React.useEffect(() => {
    if (!open) return;
    const loadOverrides = async () => {
      const promises: Array<Promise<{ unitId: string; data: any }>> = [];

      // M50: m50_u1_x1 .. m50_u1_x10
      for (let i = 1; i <= 10; i++) {
        const unitId = `m50_u1_x${i}`;
        promises.push(
          fetch(buildApiActionUrl('getUnitOverride', { unitId }), {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          })
            .then(r => r.ok ? r.json() : null)
            .then(data => ({ unitId, data }))
            .catch(() => ({ unitId, data: null }))
        );
      }

      // M10: drei Seiten
      ['m10_u1_x1_', 'm10_u1_x2_funktionen', 'm10_u2_x1_fup_uebung', 'm10_u2_x2_CAD'].forEach(unitId => {
        promises.push(
          fetch(`${API_BASE_URL}?action=getUnitOverride&unitId=${unitId}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          })
            .then(r => r.ok ? r.json() : null)
            .then(data => ({ unitId, data }))
            .catch(() => ({ unitId, data: null }))
        );
      });

      const results = await Promise.all(promises);
      const overrides: Record<string, any> = {};
      results.forEach(({ unitId, data }) => {
        if (data && (data.design || typeof data.hidden === 'boolean')) {
          overrides[unitId] = data;
        }
      });
      setUnitOverrides(overrides);
    };
    loadOverrides();
  }, [open, token]);

  const toggleModule = (modId: string) => {
    setExpandedModules(prev => ({ ...prev, [modId]: !prev[modId] }));
  };

  const moduleIds = React.useMemo(() => Object.keys((unitsData as any) || {}).sort(), []);
  const navModules = React.useMemo(() => (navMenuData as any) || null, []);

  // Hilfsfunktion: Prüft ob ein Knoten M10-Inhalte betrifft (erfordert >= 1000 Punkte)
  const isM10Node = (node: any): boolean => {
    if (!node) return false;
    const nodeId = (node.id || '').toLowerCase();
    const nodeTitle = (node.title || '').toLowerCase();
    // M10-spezifische IDs oder Titel
    if (nodeId.includes('m10') || nodeId.includes('programmierung') || nodeTitle.includes('codesys') || nodeTitle.includes('programmierung')) {
      return true;
    }
    // Auch ActionId-Checks
    if (node.actionId && (node.actionId.includes('CODESYS') || node.actionId.includes('PROGRAMMIERUNG'))) {
      return true;
    }
    return false;
  };

  // recursive renderer (returns JSX)
  const handleAction = (node: any) => {
    if (!node || !node.actionId) return;
    const slug = actionSlugOverrides[node.actionId];
    if (slug) {
      window.location.href = `/units/${slug}`;
      return;
    }
    console.warn('Aktion aus Navigation noch nicht verdrahtet:', node.actionId);
  };

  const renderNode = (node: any, rootId: string, level = 0) => {
    const id = node.id;
    const title = node.title || id;
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const actionSlug = node.actionId ? actionSlugOverrides[node.actionId] : null;
    const idOverride = slugOverrides[id] || actionSlug || null;
    const slug = !hasChildren ? idOverride || resolveNavIdToSlug(id) : null;
    const isAction = Boolean(node.actionId) && !slug;

    // Ermittle Override: M50 per ID-Mapping, M10 per slug
    const unitIdMatch = id.match(/^M50_U1_X(\d+)$/i);
    let override: any = null;
    if (unitIdMatch) {
      override = unitOverrides[`m50_u1_x${unitIdMatch[1]}`] || null;
    } else if (!hasChildren) {
      const unitIdCandidate = slug || null;
      if (unitIdCandidate && unitOverrides[unitIdCandidate]) {
        override = unitOverrides[unitIdCandidate];
      }
    }

    // Verstecke Seite wenn hidden=true im Override
    if (override?.hidden === true && !canEditContent) {
      return null;
    }

    // Verwende customTitle aus Override wenn vorhanden
    let displayTitle = title;
    if (override?.design?.customTitle) {
      displayTitle = override.design.customTitle;
    }

    // Bestimme requiredLevel: Vorrangig aus Override, sonst Default für M10 = 1000
    let requiredLevel = 0;
    if (override?.design?.requiredLevel !== undefined) {
      requiredLevel = override.design.requiredLevel;
    } else if (isM10Node(node)) {
      requiredLevel = 1000;
    }
    
    const hasAccess = canEditContent || totalScore >= requiredLevel;

    // Keine harte Ausblendung mehr für M10 – nur via hidden-Flag oder Zugriff sperren

    const handleClick = () => {
      if (hasChildren) {
        toggleModule(id);
        return;
      }
      if (isAction) {
        handleAction(node);
        handleClose();
        return;
      }
      if (slug) {
        handleClose();
      } else {
        console.warn('Keine React-Seite für Navigationseintrag', id);
      }
    };

    // Berechne den fehlenden Punkte-Wert für Anzeige
    const pointsNeeded = Math.max(0, requiredLevel - totalScore);
    const secondaryText = !hasAccess 
      ? `Benötigt ${requiredLevel} Punkte (${pointsNeeded} noch zu verdienen)`
      : undefined;

    // Render Button content (shared between both branches)
    const buttonContent = (
      <>
        <ListItemIcon>{hasChildren ? <FolderIcon /> : <DescriptionIcon />}</ListItemIcon>
        <ListItemText 
          primary={displayTitle} 
          secondary={secondaryText}
        />
        {hasChildren ? (expandedModules[id] ? <ExpandLess /> : <ExpandMore />) : null}
      </>
    );

    return (
      <div key={`${id}-${level}`}>
        {slug && hasAccess ? (
          <ListItemButton
            onClick={handleClick}
            component={RouterLink as any}
            to={`/units/${slug}`}
            sx={{ pl: 2 + level * 2 }}
            disabled={false}
          >
            {buttonContent}
          </ListItemButton>
        ) : (
          <ListItemButton
            onClick={handleClick}
            sx={{ pl: 2 + level * 2 }}
            disabled={!hasAccess}
          >
            {buttonContent}
          </ListItemButton>
        )}

        {hasChildren && (
          <Collapse in={!!expandedModules[id]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {node.children.map((c: any) => renderNode(c, rootId, level + 1))}
            </List>
          </Collapse>
        )}
      </div>
    );
  };

  return (
    <>
      <Button onClick={handleOpen} startIcon={<FolderIcon />} variant="outlined">
        Module
      </Button>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} PaperProps={{ style: { maxHeight: 600, width: 360 } }}>
        <List dense disablePadding>
          {(navModules && Array.isArray(navModules) ? navModules : moduleIds.map(m => ({ id: m, title: m, children: Object.keys(((unitsData as any)[m] || {})).sort().map(u => ({ id: u, title: u })) }))).map((mod: any) => {
            const modId = mod.id;
            const title = mod.title || modId;
            return (
              <div key={modId}>
                <ListItemButton onClick={() => toggleModule(modId)}>
                  <ListItemIcon>
                    <FolderIcon />
                  </ListItemIcon>
                  <ListItemText primary={title} />
                  {expandedModules[modId] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>

                <Collapse in={!!expandedModules[modId]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {(mod.children || []).map((child: any) => renderNode(child, modId, 1))}
                  </List>
                </Collapse>
              </div>
            );
          })}
        </List>
      </Menu>
    </>
  );
};

export default ModuleDropdown;
